import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);
  const [generatingPayment, setGeneratingPayment] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get('/invoices');
      setInvoices(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (filter === 'all') return true;
    return invoice.payment_status.toLowerCase() === filter;
  });

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return { bg: '#d4edda', color: '#155724' };
      case 'pending':
        return { bg: '#fff3cd', color: '#856404' };
      case 'overdue':
        return { bg: '#f8d7da', color: '#721c24' };
      default:
        return { bg: '#e2e3e5', color: '#383d41' };
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const handlePayWithStripe = async (invoice) => {
    setGeneratingPayment(invoice.id);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await axiosClient.post(`/invoices/${invoice.id}/create-payment-link`);

      if (response.data && response.data.payment_url) {
        // Open payment link in new tab
        window.open(response.data.payment_url, '_blank');
        setSuccessMessage(`Payment link generated for invoice ${invoice.invoice_number}`);

        // Refresh invoices to get updated stripe_payment_link
        await fetchInvoices();
      }
    } catch (err) {
      console.error('Error generating payment link:', err);
      setError(err.response?.data?.detail || 'Failed to generate payment link');
    } finally {
      setGeneratingPayment(null);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading invoices...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h1>Invoices</h1>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        View and manage all system invoices
      </p>

      {error && (
        <div style={{ padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{ padding: '10px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '4px', marginBottom: '20px' }}>
          {successMessage}
        </div>
      )}

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '8px 16px',
            backgroundColor: filter === 'all' ? '#007bff' : '#f8f9fa',
            color: filter === 'all' ? 'white' : '#333',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          All ({invoices.length})
        </button>
        <button
          onClick={() => setFilter('paid')}
          style={{
            padding: '8px 16px',
            backgroundColor: filter === 'paid' ? '#007bff' : '#f8f9fa',
            color: filter === 'paid' ? 'white' : '#333',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Paid ({invoices.filter(i => i.payment_status.toLowerCase() === 'paid').length})
        </button>
        <button
          onClick={() => setFilter('pending')}
          style={{
            padding: '8px 16px',
            backgroundColor: filter === 'pending' ? '#007bff' : '#f8f9fa',
            color: filter === 'pending' ? 'white' : '#333',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Pending ({invoices.filter(i => i.payment_status.toLowerCase() === 'pending').length})
        </button>
        <button
          onClick={() => setFilter('overdue')}
          style={{
            padding: '8px 16px',
            backgroundColor: filter === 'overdue' ? '#007bff' : '#f8f9fa',
            color: filter === 'overdue' ? 'white' : '#333',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Overdue ({invoices.filter(i => i.payment_status.toLowerCase() === 'overdue').length})
        </button>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: '12px', textAlign: 'left' }}>Invoice #</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Type</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Amount</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Paid</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Issue Date</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Due Date</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  No invoices found
                </td>
              </tr>
            ) : (
              filteredInvoices.map((invoice) => {
                const statusStyle = getStatusColor(invoice.payment_status);
                const isPaid = invoice.payment_status.toLowerCase() === 'paid';
                const hasPaymentLink = invoice.stripe_payment_link;

                return (
                  <tr key={invoice.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '12px', fontWeight: '500' }}>{invoice.invoice_number}</td>
                    <td style={{ padding: '12px' }}>
                      {invoice.invoice_type?.replace('_', ' ').toUpperCase()}
                    </td>
                    <td style={{ padding: '12px' }}>{formatCurrency(invoice.amount)}</td>
                    <td style={{ padding: '12px' }}>{formatCurrency(invoice.paid_amount)}</td>
                    <td style={{ padding: '12px' }}>
                      {invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.color,
                        fontSize: '12px',
                        textTransform: 'uppercase'
                      }}>
                        {invoice.payment_status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {isPaid ? (
                        <span style={{ fontSize: '12px', color: '#28a745' }}>Paid</span>
                      ) : hasPaymentLink ? (
                        <button
                          onClick={() => window.open(invoice.stripe_payment_link, '_blank')}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#635bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          Open Payment Link
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePayWithStripe(invoice)}
                          disabled={generatingPayment === invoice.id}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: generatingPayment === invoice.id ? '#ccc' : '#635bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: generatingPayment === invoice.id ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          {generatingPayment === invoice.id ? 'Generating...' : 'Pay with Stripe'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Outstanding</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
              {formatCurrency(invoices.reduce((sum, inv) =>
                inv.payment_status.toLowerCase() !== 'paid' ? sum + (inv.amount - inv.paid_amount) : sum, 0
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Paid</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
              {formatCurrency(invoices.reduce((sum, inv) => sum + inv.paid_amount, 0))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Revenue</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
              {formatCurrency(invoices.reduce((sum, inv) => sum + inv.amount, 0))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Invoices;
